import { zodResolver } from '@hookform/resolvers/zod';
import { CustomerType, DiscountType, PaymentMethod } from '@repo/common';
import { InvoiceGenerator } from '@repo/pdf';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useDebounce } from '@hooks/use-debounce.ts';

import { useAppDispatch, useAppSelector } from '@/features';
import {
    useAddToCartMutation,
    useAddToDraftMutation,
    useGetAllDraftsQuery,
    useGetCartProductForSaleQuery,
    useLazyClearCartQuery,
    useLazyDraftToCartQuery,
    useRemoveSingleCartItemMutation,
    useUpdateCartByIdMutation,
    useUpdateCartMutation,
} from '@/features/slice/cart/cart-api-slice';
import { useLazyGetSingleCustomerQuery } from '@/features/slice/customers/customer-api-slice.ts';
import { SingleCustomer } from '@/features/slice/customers/customer.schema.ts';
import {
    changeCustomerType,
    closeModal,
    openDiscount,
    selectedCustomer,
} from '@/features/slice/event/event-slice';
import { useCreateOrderMutation } from '@/features/slice/order';
import { useGetStoreSettingsQuery } from '@/features/slice/settings';
import { mapOrderToInvoice } from '@/utils/mappers';
import { isErrorWithMessage } from '@/utils/response-error';

import { AddToCart, AddToCartSchema, UpdateCart } from './cart-schema';

export const useCreateSale = () => {
    const [addToCart, { error: addToCartError, isLoading: addToCartLoading }] =
        useAddToCartMutation();
    const [removeCartItemApi, { isLoading: removeItemLoading }] =
        useRemoveSingleCartItemMutation();
    const [updateCartInfoApi] = useUpdateCartMutation();
    const [orderConfirm] = useCreateOrderMutation();
    const [
        clearCartApi,
        { isLoading: clearCartLoading, isError: clearCartError },
    ] = useLazyClearCartQuery();
    const [updateCartById] = useUpdateCartByIdMutation();
    const [
        addToDraft,
        { isLoading: draftSaveLoading, isError: draftSaveError },
    ] = useAddToDraftMutation();

    const { data: storeData } = useGetStoreSettingsQuery({});

    const [
        draftToCart,
        {
            isLoading: draftToCartLoading,
            isError: draftToCartError,
            isSuccess: drafToCartSuccess,
        },
    ] = useLazyDraftToCartQuery();
    const {
        data: allDrafts,
        isLoading: draftLoading,
        isError: draftError,
    } = useGetAllDraftsQuery('drafts');
    const [
        getSingleCustomerApi,
        { data: customerDetails, isLoading: customerLoading },
    ] = useLazyGetSingleCustomerQuery();

    const {
        data: cartData,
        isLoading: cartDataLoading,
        isError,
        isSuccess,
        error,
    } = useGetCartProductForSaleQuery('cart');

    const [singleCustomerDetails, setSingleCustomerDetails] = useState<
        SingleCustomer['data']
    >({
        address: '',
        email: '',
        id: '',
        name: '',
        phone_number: '',
        customer_type: CustomerType.RETAIL,
    });
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [discountValue, _setDiscountValue] = useState(0);
    const [discount, setDiscount] = useState<{
        type: DiscountType;
        value: number;
    }>({
        type: DiscountType.Fixed,
        value: 0,
    });
    const [quantity, setQuantity] = useState(0);

    const [paidValue, setPaidValue] = useState(0);
    const [cashBack, setCashBack] = useState(0);
    const dispatch = useAppDispatch();
    const [updatedCart, setUpdatedCart] = useState({});

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(
        PaymentMethod.CASH,
    );
    const [transactionId, setTransactionId] = useState('');

    const discountOpen = useAppSelector(
        (state) => state.eventSlice.discountOpen,
    );
    const customerType = useAppSelector(
        (state) => state.eventSlice.customerType,
    );

    const {
        handleSubmit,
        register,
        watch,
        reset: cartReset,
        setValue: setProductValue,
        control,
    } = useForm<AddToCart>({
        resolver: zodResolver(AddToCartSchema),
        defaultValues: {
            purchase_id: '',
            quantity: 1,
            type: CustomerType.WALKIN,
            price: 0,
            discount_value: 0,
            discount_type: DiscountType.Fixed,
        },
    });

    useEffect(() => {
        if (customerDetails && !customerLoading) {
            setSingleCustomerDetails(customerDetails);
        }
    }, [customerDetails, customerLoading]);

    useEffect(() => {
        dispatch(changeCustomerType(cartData?.data?.info?.customer_type));
    }, [cartData?.data?.info?.customer_type]);

    useEffect(() => {
        setDiscount((prev) => ({
            ...prev,
            value: cartData?.data?.info?.discount_value || 0,
            type: cartData?.data?.info?.discount_type || DiscountType.Fixed,
        }));
        resetCartForm();
    }, [cartData?.data?.info?.discount_value]);

    useEffect(() => {
        setCashBack(paidValue - (cartData?.data?.info?.total_price || 0));
    }, [paidValue, cartData?.data?.info?.total_price]);

    const resetCartForm = () => {
        setProductValue('purchase_id', '');
        setProductValue('quantity', 1);
        setProductValue('price', 0);
    };
    const [removeId, setRemoveId] = useState('');
    const handleRemoveCartItem = useCallback(
        (id: string) => {
            removeCartItemApi(id);
            setRemoveId(id);
        },
        [removeCartItemApi],
    );

    const changeSelectedProduct = useCallback(
        (data: string) => {
            setProductValue('purchase_id', data);
        },
        [setProductValue],
    );

    const updateCart = useCallback(() => {
        updateCartInfoApi(updatedCart);
    }, [
        customerType,
        updateCartInfoApi,
        cartData?.data.info.note,
        setUpdatedCart,
    ]);

    const changeSelectedCustomer = useCallback(
        (customerId: string) => {
            getSingleCustomerApi(customerId);
            setSelectedCustomerId(customerId);
            // updateCart();
            updateCartInfoApi({
                customer_id: customerId || null,
                customer_type: customerType as CustomerType,
                discount_id: null,
                discount_type: discount.type,
                discount_value: discount.value,
                tax: '',
                note: '',
            });
        },
        [discount, getSingleCustomerApi, updateCart],
    );

    const debouncedUpdateCart = useDebounce((discount) => {
        // setUpdatedCart({
        //   customer_id: selectedCustomerId || null,
        //   customer_type: customerType as CustomerType,
        //   discount_id: null,
        //   discount_type: discount.type,
        //   discount_value: discount.value,
        //   tax: cartData?.data.info.tax,
        //   note: cartData?.data.info.note,
        // });

        updateCartInfoApi({
            customer_id: selectedCustomerId || null,
            customer_type: customerType as CustomerType,
            discount_id: null,
            discount_type: discount.type,
            discount_value: discount.value,
            tax: '',
            note: '',
        });

        dispatch(openDiscount(!openDiscount));
    }, 1000);

    const handleDiscountChange = (field: keyof typeof discount, value: any) => {
        let updatedValue = value;
        if (field === 'value' && cartData?.data.info.sub_total_price) {
            const numericValue = Number(value);
            if (
                discount.type === 'fixed' &&
                numericValue > cartData?.data.info.sub_total_price
            ) {
                updatedValue = cartData?.data.info.sub_total_price;
            } else if (discount.type === 'percentage' && numericValue > 100) {
                updatedValue = 100;
            }
        }

        const updatedDiscount = { ...discount, [field]: updatedValue };
        setDiscount(updatedDiscount);
        debouncedUpdateCart(updatedDiscount);
    };

    const handlePaidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const paidAmount = Number(e);

        if (!paidAmount || isNaN(paidAmount)) {
            setPaidValue(0);
            setCashBack(0);
        } else {
            setPaidValue(paidAmount);
            setCashBack(paidAmount - (cartData?.data?.info?.total_price ?? 0));
        }
    };

    const handleAddToCart = useCallback(
        async (data: AddToCart) => {
            if (watch('purchase_id')) {
                await addToCart({
                    ...data,
                    type: customerType,
                });
                cartReset();
            } else {
                toast.warning('Please select your product first.', {
                    style: { background: '#FFC107', fontSize: '16px' },
                });
            }
        },
        [watch, customerType, addToCart],
    );

    const handleSubmitOrder = useCallback(
        async (paidAmount: number) => {
            if (!cartData?.data.items?.length) {
                toast.warning('Your cart is empty.', {
                    style: { background: '#FFC107', fontSize: '16px' },
                });
                return;
            }

            if (customerType === CustomerType.RETAIL && !selectedCustomerId) {
                toast.warning('Please select your customer first.', {
                    style: { background: '#FFC107', fontSize: '16px' },
                });
                return;
            }

            // return;

            const res = await orderConfirm({
                paid: paidAmount,
                payment_method: selectedPaymentMethod,
                transaction_id: transactionId,
            }).unwrap();

            if (res) {
                // generateReceipt(
                //   {
                //     orderNumber: cartData.data.info.id,
                //     date: new Date().toLocaleString(),
                //     items: cartData.data.items,
                //     subtotal: cartData.data.info.sub_total_price,
                //     tax: 0,
                //     total: cartData.data.info.total_price,
                //     paymentMethod: 'Hand Cash',
                //   },
                //   'print',
                // );

                const invoice = new InvoiceGenerator(
                    mapOrderToInvoice(res.data, storeData?.data as any),
                );
                invoice.print();

                dispatch(changeCustomerType(CustomerType.WALKIN));
                dispatch(selectedCustomer(CustomerType.WALKIN));
                setSingleCustomerDetails({
                    address: '',
                    email: '',
                    id: '',
                    name: '',
                    phone_number: '',
                    customer_type: CustomerType.WALKIN,
                });
                setPaidValue(0);
            }
        },
        [
            cartData?.data.items?.length,
            dispatch,
            orderConfirm,
            selectedCustomerId,
        ],
    );

    const updateCartSingleItem = useCallback(async (data: UpdateCart) => {
        if (data.quantity < 1) return;

        await updateCartById({
            id: data.id,
            quantity: data.quantity,
            price: data.price,
            discount_type: data.discount_type,
            discount_value: data.discount_value,
            type: data.type,
        });
    }, []);

    const handleChangeQuantity = (data: UpdateCart) => {
        if (data.quantity > 0) updateSingleCartQuantityChange(data);
    };

    const updateSingleCartQuantityChange = useDebounce(
        async (data: UpdateCart) => {
            await updateCartById({
                id: data.id,
                quantity: data.quantity,
                price: data.price,
                discount_type: data.discount_type,
                discount_value: data.discount_value,
                type: data.type,
            });
        },
        1000,
    );

    const handleSaveDraft = useCallback(async (data: string) => {
        const { statusCode } = await addToDraft({
            draft_note: data,
        }).unwrap();

        if (statusCode == 201) {
            dispatch(closeModal());
            toast.success('Draft saved successfully');
        }
    }, []);

    const handleDraftToCart = useCallback(async (id: string) => {
        const res = await draftToCart(id).unwrap();
        if (res) {
            toast.success('Draft added to cart successfully');
            dispatch(closeModal());
        }
    }, []);

    const noteDebounce = useDebounce((note: string) => {
        setUpdatedCart((prev) => ({ ...prev, note }));
    }, 500);
    const handleNoteChange = (note: string) => {
        noteDebounce(note);
    };
    return {
        addToCartError: isErrorWithMessage(addToCartError),
        addToCartLoading,
        changeSelectedProduct,
        changeSelectedCustomer,
        handleSubmit: handleSubmit(handleAddToCart),
        cartData,
        cartDataLoading,
        register,
        productValue: watch('purchase_id'),
        setProductValue,
        customerType,
        changeCustomerTypeName: (type: string) => {
            dispatch(changeCustomerType(type));
        },
        isError,
        isSuccess,
        saleApiError: isErrorWithMessage(error),
        handleRemoveCartItem,
        singleCustomerDetails,
        clearCartApi,
        clearCartLoading,
        clearCartError,
        customerLoading,
        handleSubmitOrder,
        selectedCustomerId,
        handleDiscountChange,
        discountOpen,
        discountValue,
        paidValue,
        handlePaidChange,
        dispatch,
        cashBack,
        discount,
        control,
        updateCartSingleItem,
        handleChangeQuantity,
        quantity,
        setQuantity,
        handleSaveDraft,
        draftSaveLoading,
        draftSaveError,
        allDrafts,
        draftLoading,
        draftError,
        handleDraftToCart,
        draftToCartLoading,
        draftToCartError,
        drafToCartSuccess,
        removeItemLoading,
        handleNoteChange,
        removeId,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        transactionId,
        setTransactionId,
    };
};
